// Platform monthly orders — GET /platform/reports/monthly-orders: one row
// per month in the period (orders, revenue, the restaurants and stores
// that traded, the channel split), a totals row, narrowed to one
// restaurant with restaurantId=. The shared typed table renders it;
// CSV/XLSX through format=.

import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { api, type Report } from '../../lib/api';
import { downloadReport, ExportButtons, PeriodPicker, periodParams, ReportTable, type Period } from '../restaurant/report';
import type { Restaurant } from '../restaurant/types';

const PATH = '/platform/reports/monthly-orders';

export function PlatformReports() {
  const [period, setPeriod] = useState<Period>({ preset: 'this_month', from: '', to: '' });
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<Restaurant[]>([]);
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  const params: Record<string, string> = { ...periodParams(period), ...(restaurant ? { restaurantId: restaurant.restaurantId } : {}) };
  const key = JSON.stringify(params);
  const ready = period.preset !== null || (period.from !== '' && period.to !== '');

  useEffect(() => {
    if (!ready) return;
    const mine = ++seq.current;
    setReport(null);
    api.get<Report>(PATH, params)
      .then((r) => { if (mine === seq.current) { setReport(r); setError(null); } })
      .catch((e: unknown) => { if (mine === seq.current) setError(e instanceof Error ? e.message : 'Failed to load.'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ready]);

  useEffect(() => {
    if (!query.trim()) { setMatches([]); return; }
    let live = true;
    const t = setTimeout(() => {
      api.get<Restaurant[]>('/restaurants', { query: query.trim() })
        .then((r) => { if (live) setMatches(r.slice(0, 8)); })
        .catch(() => { if (live) setMatches([]); });
    }, 200);
    return () => { live = false; clearTimeout(t); };
  }, [query]);

  async function exportAs(format: 'csv' | 'xlsx') {
    try {
      await downloadReport(PATH, params, format, `monthly-orders${report ? `-${report.range.from}-${report.range.to}` : ''}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed.');
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">Platform · Reports</div>
          <h1>Monthly orders</h1>
        </div>
        <span style={{ marginLeft: 'auto' }}>
          <ExportButtons onExport={(f) => void exportAs(f)} disabled={!report} />
        </span>
      </div>

      <div style={{ marginBottom: 12 }}>
        <PeriodPicker value={period} onChange={setPeriod} presets={['this_month', 'last_month', 'last_30_days']} />
      </div>
      <div className="filters" style={{ marginBottom: 16 }} data-section="restaurant-filter">
        {restaurant ? (
          <span className="chip" data-narrowed={restaurant.restaurantId} style={{ '--dot': 'var(--primary)' } as React.CSSProperties}>
            {restaurant.name}
            <button className="btn btn-ghost btn-sm" aria-label="Every restaurant" style={{ padding: '0 4px' }}
              onClick={() => setRestaurant(null)}>
              <X size={12} />
            </button>
          </span>
        ) : (
          <>
            <input className="input" aria-label="Narrow to a restaurant" placeholder="Narrow to a restaurant…" value={query}
              style={{ maxWidth: 280 }} onChange={(e) => setQuery(e.target.value)} />
            {matches.map((r) => (
              <button key={r.restaurantId} className="filter" onClick={() => { setRestaurant(r); setQuery(''); }}>
                {r.name}
              </button>
            ))}
          </>
        )}
      </div>

      {error && <div className="err" role="alert">{error}</div>}
      {!ready && <div className="loading">Pick both dates.</div>}
      {ready && report === null && !error && <div className="loading">Loading…</div>}
      {report && (
        <>
          <p style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: '0 0 10px' }}>
            {report.range.from} → {report.range.to} · accepted, in-progress and completed orders by business date
          </p>
          <ReportTable report={report} empty="No orders on the platform in this period." />
        </>
      )}
    </>
  );
}
